import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSupplier } from "../../context/SupplierContext";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import * as Lucide from "lucide-react";
import { Check, Sparkles, Globe, Users, Link as LinkIcon, ChevronDown, Store, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/* Shared App Store surface for buyers + sellers.
   Buyer governance (Feb 2026):
     • R1 — In storefront, the App Store is unreachable. The page renders a
       "Switch to Hub" notice instead of the catalog grid.
     • R3 — In Hub, every app is marked "Coming soon" and Install/Uninstall +
       Configure + Edit-scope buttons are removed. The store is read-only.
   Sellers keep the full 3-scope install flow (BUYER_GLOBAL · SELLER_GLOBAL ·
   PER_CONNECTION) unchanged. */
const BUYER_APPS_LOCKED = true; // mirrors backend BUYER_APPS_INSTALL_LOCKED

const SCOPE_META = {
  BUYER_GLOBAL:   { label: "Global",        Icon: Globe,    tone: "text-foreground/70", desc: "Works across every supplier you're connected to." },
  SELLER_GLOBAL:  { label: "Global",        Icon: Globe,    tone: "text-foreground/70", desc: "Applies to every buyer on your portal." },
  PER_CONNECTION: { label: "Per-supplier",  Icon: LinkIcon, tone: "text-accent",        desc: "Choose which suppliers (or buyers) this applies to." },
};

export default function AppStore() {
  const nav = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "buyer";
  const isBuyer = role === "buyer";
  const { connections, active } = useSupplier();  // buyer-only; falls back to defaults
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [modalApp, setModalApp] = useState(null);        // app opened in per-connection install modal
  const [sellerConnections, setSellerConnections] = useState([]);

  const load = async () => {
    const { data } = await api.get("/apps");
    setApps(data);
  };
  useEffect(() => { load(); }, []);

  // Tell the sidebar (PortalShell) to re-fetch /apps so install/uninstall
  // takes effect immediately without a navigation or full reload.
  const notifySidebar = () => window.dispatchEvent(new Event("apps-changed"));

  // Seller role needs to fetch its own connections for the per-buyer modal
  useEffect(() => {
    if (!isBuyer) {
      api.get("/tenant/connections").then(r => setSellerConnections(r.data || [])).catch(() => {});
    }
  }, [isBuyer]);

  const targets = isBuyer ? connections : sellerConnections;   // what PER_CONNECTION can scope to

  // When buyer is viewing inside an active supplier, preselect that in the modal
  const defaultSelectedIds = useMemo(() => {
    if (isBuyer && active) return [active.id];
    return [];
  }, [isBuyer, active]);

  const onInstallClick = (app) => {
    if (app.scope === "PER_CONNECTION") {
      // Sellers: app installs cleanly even with zero connected buyers — an
      // empty `connection_ids` array means "applies to every current and
      // future buyer". Don't gate the install on having a buyer first.
      // Buyers without any connected supplier still need a target, since
      // a buyer-side per-supplier app can't run without a supplier scope.
      if (!isBuyer) {
        // Seller path — go straight through the modal so they can explicitly
        // pick "All buyers" or specific ones. Even when targets.length === 0
        // the modal still works ("All buyers" mode installs with [] scope).
        setModalApp(app);
        return;
      }
      if (!targets.length) {
        toast.error("Connect to a supplier first");
        return;
      }
      setModalApp(app);
      return;
    }
    // Global — install immediately
    api.post(`/apps/${app.id}/install`, {})
      .then(() => {
        toast.success(`${app.name} installed`);
        load(); notifySidebar();
        // If the app ships a setup screen, send the user straight there so
        // they can finish configuration before their first order hits.
        if (app.setup_route) nav(app.setup_route);
      })
      .catch(() => toast.error("Install failed"));
  };

  const onUninstall = (app) => {
    // Catalog is "core" for buyers (it's the default way they browse what
    // sellers offer) but it's a CHOICE for sellers — sellers can opt out.
    if (app.id === "catalog" && isBuyer) {
      return toast.error("Core app — can't uninstall");
    }
    api.delete(`/apps/${app.id}/install`).then(() => {
      toast.success(`${app.name} uninstalled`);
      load(); notifySidebar();
    });
  };

  const onInstallWithScope = async (selectedIds) => {
    try {
      await api.post(`/apps/${modalApp.id}/install`, { connection_ids: selectedIds });
      toast.success(`${modalApp.name} installed`);
      setModalApp(null);
      load(); notifySidebar();
    } catch { toast.error("Install failed"); }
  };

  const onUpdateScope = async (app, selectedIds) => {
    await api.patch(`/apps/${app.id}/install`, { connection_ids: selectedIds });
    toast.success("Scope updated");
    load(); notifySidebar();
  };

  const categories = ["all", ...Array.from(new Set(apps.map(a => a.category)))];
  const inStorefront = isBuyer && !!active;
  const sellerId = active?.tenant_owner_user_id;
  const buyerLocked = isBuyer && BUYER_APPS_LOCKED; // R3 — read-only catalog

  // Apps the active seller has enabled (cross-portal apps only — non-cross
  // apps are buyer-side only and unaffected by which seller is active).
  const sellerHasApp = (a) => {
    if (!a.cross_portal_visible) return true;  // not seller-dependent
    return (a.enabled_by_supplier || []).some(s => s.seller_id === sellerId);
  };

  const visibleApps = useMemo(() => {
    if (!inStorefront) return apps;
    // (Unreachable for buyer — buyer-in-storefront early-returns below.
    //  Kept for seller parity.)
    return apps.filter(a => {
      if (a.id === "suppliers") return false;
      if (a.cross_portal_visible) return sellerHasApp(a);
      return true;
    });
  }, [apps, inStorefront, sellerId]);

  // R1 — In storefront, the App Store is unreachable for buyers. Render a
  // switch-to-Hub notice and bail. NOTE: this must live AFTER all hooks so
  // the hook call order stays stable across renders.
  if (isBuyer && inStorefront) {
    return (
      <>
        <PageHeader title="App Store" subtitle="Available only outside a supplier's storefront." />
        <div className="page-shell">
          <div
            className="max-w-2xl mx-auto rounded-2xl border border-border bg-white p-10 text-center"
            data-testid="appstore-storefront-blocked"
          >
            <div className="w-14 h-14 mx-auto rounded-2xl bg-foreground/5 grid place-items-center">
              <Store className="w-6 h-6 text-foreground/70" />
            </div>
            <h2 className="display text-2xl mt-5">You're inside {active.tenant_name}'s storefront</h2>
            <p className="text-foreground/65 mt-3 leading-relaxed">
              The App Store is part of your verqo Hub — your own account, independent of any
              supplier. While you're shopping inside a supplier's portal, you only see what
              that supplier has enabled for you.
            </p>
            <p className="text-foreground/55 text-sm mt-2">
              Exit this storefront from the supplier switcher to open the App Store.
            </p>
            <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
              <Button
                onClick={() => nav("/buyer")}
                data-testid="appstore-back-storefront"
                className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5"
              >
                Back to {active.tenant_name} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const filtered = filter === "all" ? visibleApps : visibleApps.filter(a => a.category === filter);
  const installed = visibleApps.filter(a => a.installed);
  const hiddenInStorefront = inStorefront ? (apps.length - visibleApps.length) : 0;

  return (
    <>
      <PageHeader
        title="App Store"
        subtitle={isBuyer
          ? "Extend your verqo portal — global tools work across every supplier; per-supplier tools scope per connection."
          : "Extend your seller portal — global tools apply to all your buyers; per-buyer tools let you pick which buyers see them."}
      />
      <div className="p-8 space-y-6">

        {/* Active-supplier hint for buyers */}
        {isBuyer && active && (
          <div
            className="rounded-xl border p-3.5 flex items-start gap-3 text-[13px]"
            style={{ borderColor: (active.branding?.accent_color || "#e8ff3a") + "66", background: (active.branding?.primary_color || "#0a1f1c") + "08" }}
            data-testid="appstore-supplier-hint"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="mono uppercase tracking-[0.18em] text-[10.5px] text-foreground/55">Currently inside</span>
                <strong>{active.tenant_name}</strong>
                <span className="mono uppercase tracking-[0.18em] text-[9.5px] px-1.5 py-0.5 rounded bg-foreground/8 text-foreground/65">storefront mode</span>
              </div>
              <div className="text-foreground/65 mt-1.5 leading-relaxed">
                Showing only apps {active.tenant_name} has enabled, plus your buyer-only tools.
                {hiddenInStorefront > 0 && <> {hiddenInStorefront} app{hiddenInStorefront === 1 ? "" : "s"} hidden — </>}
                {hiddenInStorefront > 0 && (
                  <strong className="font-medium"> exit this storefront via the supplier switcher to see all apps.</strong>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="rounded-2xl card-ink p-7 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-accent/20 blur-3xl"/>
          <div className="relative flex items-center justify-between gap-6 flex-wrap">
            <div>
              <span className="chip glow-chip mono">{buyerLocked ? "PREVIEW · COMING SOON" : "CORE + APPS"}</span>
              <h2 className="display text-3xl mt-3 max-w-xl">
                {buyerLocked ? "A preview of every app coming to verqo." : "Build the wholesale stack that fits your business."}
              </h2>
              <p className="text-background/70 text-sm mt-2 max-w-md">
                {buyerLocked
                  ? "Browse what's on the way. Self-serve install for buyers is rolling out app by app — tap Notify me on anything you want first access to."
                  : `Invoicing, capital, insurance, score and shipping are always-on. Everything else is opt-in — global or per-${isBuyer ? "supplier" : "buyer"}.`}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs mono uppercase tracking-widest text-accent/80">
                {buyerLocked ? "In preview" : "Installed"}
              </div>
              <div className="display text-5xl mt-1">
                {buyerLocked ? apps.length : <>{installed.length}<span className="text-2xl text-background/40">/{apps.length}</span></>}
              </div>
            </div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 flex-wrap">
          {categories.map(c => (
            <button key={c} onClick={()=>setFilter(c)} data-testid={`apps-cat-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs capitalize border transition-colors ${filter===c?"bg-foreground text-background border-foreground":"bg-white border-border hover:border-foreground/40"}`}>
              {c}
            </button>
          ))}
        </div>

        {/* Apps grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(app => {
            const Icon = Lucide[app.icon] || Sparkles;
            const scopeMeta = SCOPE_META[app.scope] || SCOPE_META.BUYER_GLOBAL;
            const ScopeIcon = scopeMeta.Icon;
            const myRole = isBuyer ? "buyer" : "seller";
            // R3 — for buyers, ALL apps render as "coming soon" until the
            // install lock is lifted. Sellers keep the real flag.
            const comingSoon = buyerLocked
              || app.coming_soon === true
              || (Array.isArray(app.coming_soon_for) && app.coming_soon_for.includes(myRole));
            const scopedSummary = app.installed && app.scope === "PER_CONNECTION"
              ? (app.installed_all ? `All ${isBuyer ? "suppliers" : "buyers"}` : `${app.connection_ids.length} ${isBuyer ? "supplier" : "buyer"}${app.connection_ids.length === 1 ? "" : "s"}`)
              : null;
            return (
              <div key={app.id} className={`rounded-2xl bg-white border border-border p-5 flex flex-col ${comingSoon ? "opacity-75" : ""}`} data-testid={`app-${app.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="w-11 h-11 rounded-xl bg-foreground text-accent grid place-items-center">
                    <Icon className="w-5 h-5"/>
                  </div>
                  {comingSoon && (
                    <span className="chip bg-foreground/5 border border-foreground/15 text-foreground/70" data-testid={`coming-soon-${app.id}`}>
                      Coming soon
                    </span>
                  )}
                  {!comingSoon && app.installed && <span className="chip bg-accent/30 border border-accent/40"><Check className="w-3 h-3"/>Installed</span>}
                </div>
                <div className="display text-lg mt-4">{app.name}</div>
                <div className="flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-widest">
                  <span className="text-foreground/50">{app.category}</span>
                  <span className="text-foreground/30">·</span>
                  <span className={`inline-flex items-center gap-1 ${scopeMeta.tone}`}>
                    <ScopeIcon className="w-2.5 h-2.5"/>{scopeMeta.label}
                  </span>
                </div>
                <p className="text-sm text-foreground/70 mt-3 leading-relaxed flex-1">{app.tagline}</p>
                {/* Cross-portal enablement badge — when a connected supplier
                    has installed this app and it's relevant to buyers. */}
                {isBuyer && app.enabled_by_supplier && app.enabled_by_supplier.length > 0 && (
                  <div className="mt-3 text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid={`enabled-by-${app.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                    Enabled by{" "}
                    <strong className="text-emerald-800 font-medium">
                      {app.enabled_by_supplier.length === 1
                        ? app.enabled_by_supplier[0].seller_name
                        : `${app.enabled_by_supplier.length} suppliers`}
                    </strong>
                  </div>
                )}
                {scopedSummary && (
                  <div className="mt-3 text-[11.5px] text-foreground/55 flex items-center gap-1.5">
                    <LinkIcon className="w-3 h-3"/>Scope: <strong className="text-foreground/80">{scopedSummary}</strong>
                    {!app.installed_all && !buyerLocked && (
                      <button
                        onClick={() => setModalApp(app)}
                        className="ml-1 underline decoration-foreground/30 hover:decoration-foreground text-[11px]"
                        data-testid={`edit-scope-${app.id}`}
                      >
                        edit
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-5 flex items-center justify-between">
                  <span className="display text-base">{app.price}</span>
                  <div className="flex gap-1.5">
                    {comingSoon ? (
                      <Button
                        size="sm"
                        disabled
                        data-testid={`coming-soon-btn-${app.id}`}
                        className="rounded-full bg-foreground/10 text-foreground/50 cursor-not-allowed"
                      >
                        Notify me
                      </Button>
                    ) : (
                      <>
                        {app.installed && app.setup_route && (
                          <Button
                            onClick={() => nav(app.setup_route)}
                            size="sm"
                            variant="outline"
                            data-testid={`configure-${app.id}`}
                            className="rounded-full"
                          >
                            Configure
                          </Button>
                        )}
                        <Button
                          onClick={() => app.installed ? onUninstall(app) : onInstallClick(app)}
                          size="sm"
                          data-testid={`toggle-${app.id}`}
                          className={`rounded-full ${app.installed ? "bg-secondary text-foreground hover:bg-secondary/70" : "bg-foreground text-background hover:bg-foreground/90"}`}
                        >
                          {app.installed ? "Uninstall" : "Install"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border-2 border-dashed border-border p-7 text-center text-foreground/60">
          <div className="text-sm">Looking for an integration not listed?</div>
          <div className="text-xs mt-1">Email <span className="mono text-foreground">apps@verqo.com</span> — we ship most requests in under two weeks.</div>
        </div>
      </div>

      {/* Per-connection install modal */}
      {modalApp && (
        <ScopePickerModal
          app={modalApp}
          isBuyer={isBuyer}
          targets={targets}
          defaultSelectedIds={modalApp.installed ? (modalApp.connection_ids || []) : defaultSelectedIds}
          onClose={() => setModalApp(null)}
          onSubmit={(ids) => modalApp.installed ? onUpdateScope(modalApp, ids).then(() => setModalApp(null)) : onInstallWithScope(ids)}
        />
      )}
    </>
  );
}

// ── Per-connection scope picker ─────────────────────────────────────────
function ScopePickerModal({ app, isBuyer, targets, defaultSelectedIds, onClose, onSubmit }) {
  const allIds = targets.map(t => t.id);
  const initialMode = defaultSelectedIds.length === 0 || defaultSelectedIds.length === allIds.length ? "all" : "pick";
  const [mode, setMode] = useState(initialMode);
  const [selected, setSelected] = useState(defaultSelectedIds);

  const label = isBuyer ? "supplier" : "buyer";

  const getName = (t) => isBuyer ? (t.tenant_name || t.tenant_slug) : (t.buyer_name || t.buyer_email || t.buyer_id);

  const toggleId = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = () => {
    onSubmit(mode === "all" ? [] : selected);
  };

  // Seller with zero connected buyers — we still let them install (the
  // app simply auto-applies to every buyer that connects from now on).
  // The "Pick specific buyers" option doesn't make sense in this state.
  const hasNoTargets = targets.length === 0;
  const allRadioLabel = hasNoTargets
    ? (isBuyer ? "All my suppliers" : "All buyers (current + future)")
    : `All my ${label}s (${targets.length})`;
  const allRadioDesc  = hasNoTargets
    ? (isBuyer
        ? "Applies to every supplier you connect to."
        : "Applies automatically to every buyer that connects to your portal — now and in the future.")
    : `Default — applies to every ${label} you're connected to, now and in the future.`;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scope-picker-modal">
        <DialogHeader>
          <DialogTitle>{app.installed ? `Edit scope · ${app.name}` : `Install ${app.name}`}</DialogTitle>
          <DialogDescription>
            {hasNoTargets
              ? (isBuyer
                  ? "Once you connect a supplier this app will start working for them automatically."
                  : "You don't have any buyers connected yet — that's fine. This app will activate for every buyer the moment they connect to your portal.")
              : `Pick which ${label}s this app should apply to.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 mt-2">
          <label className="flex items-start gap-3 p-3 rounded-xl border border-foreground/15 cursor-pointer hover:bg-secondary/30" data-testid="scope-mode-all">
            <input type="radio" checked={mode === "all"} onChange={() => setMode("all")} className="mt-1"/>
            <div>
              <div className="font-medium text-[14px]">{allRadioLabel}</div>
              <div className="text-[12px] text-foreground/55">{allRadioDesc}</div>
            </div>
          </label>
          {!hasNoTargets && (
            <label className="flex items-start gap-3 p-3 rounded-xl border border-foreground/15 cursor-pointer hover:bg-secondary/30" data-testid="scope-mode-pick">
              <input type="radio" checked={mode === "pick"} onChange={() => setMode("pick")} className="mt-1"/>
              <div className="flex-1">
                <div className="font-medium text-[14px]">Pick specific {label}s</div>
                <div className="text-[12px] text-foreground/55">Only applies to the ones you select.</div>
              </div>
            </label>
          )}
        </div>

        {mode === "pick" && !hasNoTargets && (
          <div className="mt-3 max-h-60 overflow-y-auto border border-foreground/10 rounded-xl bg-secondary/20 p-2 space-y-1" data-testid="scope-target-list">
            {targets.map(t => (
              <label key={t.id} className="flex items-center gap-2.5 p-2 rounded hover:bg-white cursor-pointer text-[13px]">
                <input
                  type="checkbox"
                  checked={selected.includes(t.id)}
                  onChange={() => toggleId(t.id)}
                  data-testid={`scope-target-${t.id}`}
                />
                <span>{getName(t)}</span>
                {isBuyer && t.default_terms && (
                  <span className="ml-auto text-[10.5px] mono text-foreground/50">{t.default_terms.replace("NET_", "Net-")}</span>
                )}
              </label>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="scope-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={mode === "pick" && selected.length === 0}
            data-testid="scope-submit"
            className="rounded-full bg-foreground text-background"
          >
            {app.installed ? "Save" : "Install"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
